import { Box, Chip, Typography } from "@mui/material";
import { HStack } from "@zfp-akademie-a-s/zfp-design";
import { Spacing } from "Infrastructure/Themes/Themes";
import { type ResourcePathType, Resources, useResource } from "Infrastructure/Translations/Resources";
import { inflectYears } from "Infrastructure/Utils/LanguageUtils";
import { useClientBirthDate } from "../Hooks/useClientBirthDate";
import { TimelineAxisCell, TimelineContentCell, TimelineDateCell, TimelineRow, axisMarkerRing } from "./TimelineRow";
import type { FutureHorizon } from "./utils/buildTimelineItems";
import { getClientAge } from "./utils/dates";

const Timeline = Resources.AnalysisDetail.Goals.Timeline;

const HORIZON_TITLES: Record<FutureHorizon, ResourcePathType> = {
  short: Timeline.ShortTermGoals,
  middle: Timeline.MidTermGoals,
  long: Timeline.LongTermPoint,
};

type Props = {
  horizon: FutureHorizon;
  startDate: Date;
  isReversed: boolean;
};

export const HorizonDivider = ({ horizon, startDate, isReversed }: Props) => {
  const { t } = useResource();
  const birthDate = useClientBirthDate();

  return (
    // The gap separates the divider from the previous horizon, which is below it in descending order.
    // Without past goals the divider can open or close the timeline, where the gap would only add empty space.
    <TimelineRow
      height={46}
      alignItems="center"
      {...(isReversed ? { mb: Spacing.XL } : { mt: Spacing.XL })}
      sx={{ "&:first-child": { mt: 0 }, "&:last-child": { mb: 0 } }}
    >
      <TimelineDateCell />

      <TimelineAxisCell>
        <Box width={14} height="1px" bgcolor="grey.300" sx={axisMarkerRing} />
      </TimelineAxisCell>

      <TimelineContentCell>
        <HStack alignItems="center" gap={Spacing.M}>
          <Typography variant="overline" flexShrink={0} color="text.secondary" textTransform="uppercase" letterSpacing="0.06em">
            {t(HORIZON_TITLES[horizon])}
          </Typography>
          {birthDate && <Chip size="small" label={inflectYears(getClientAge(birthDate, startDate), t)} sx={{ flexShrink: 0 }} />}
          <Box flex={1} borderTop="1px dashed" borderColor="divider" />
        </HStack>
      </TimelineContentCell>
    </TimelineRow>
  );
};
